import { h, Component } from 'preact';
import { reaction } from 'mobx';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import {
    drugSetKey,
    drugsToCheckPayload,
    proposalToPrefill,
    severityClass,
    mergeWarnings,
    strengthMatches,
    preselectedLId,
    packageLabel,
} from '../../lib/prescriptionAi';

// Debounce for the interaction check after the drawer's drug set changes.
const CHECK_DEBOUNCE_MS = 750;

const SOURCE_LABEL = {
    journal: 'journal',
    prescription: 'resept',
    sale: 'salg',
};

@observer
class PrescriptionAiAssistant extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loadingProposal: false,
            proposalDone: false,
            proposalError: '',
            proposals: [],
            proposalWarnings: [],
            checkWarnings: [],
            currentMedications: [],
            weight: null,
            summary: '',
            claudeLogId: 0,
            rating: 0,
            checking: false,
            checkError: '',
            lastCheckedKey: null,
            selectedPackage: {},
            accepted: {},
            dismissed: {},
            showMedications: false,
        };
        this.checkTimer = null;
        this.unmounted = false;
        this.lastSeenKey = null;
        this.disposeKeyReaction = null;
    }

    componentDidMount() {
        this.lastSeenKey = drugSetKey(this.props.drugs);
        this.trackDrugs();
        if (this.lastSeenKey) {
            this.scheduleCheck();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.drugs !== this.props.drugs) {
            // A new array (line added or removed): track its lines instead of the old ones.
            this.trackDrugs();
        }
        this.checkKeyChange();
    }

    componentWillUnmount() {
        this.unmounted = true;
        if (this.checkTimer) {
            clearTimeout(this.checkTimer);
        }
        if (this.disposeKeyReaction) {
            this.disposeKeyReaction();
            this.disposeKeyReaction = null;
        }
    }

    // The store edits a drug line IN PLACE (localModel.updateObjectKeyArrFieldValue), so on a
    // dosage edit the props reference never changes and componentDidUpdate cannot see it.
    // Track the observable lines directly: this reaction re-evaluates the key whenever a
    // field the key depends on (lId, name, dosage) changes on any line.
    trackDrugs = () => {
        if (this.disposeKeyReaction) {
            this.disposeKeyReaction();
        }
        this.disposeKeyReaction = reaction(() => drugSetKey(this.props.drugs), () => this.checkKeyChange());
    }

    checkKeyChange = () => {
        const key = drugSetKey(this.props.drugs);
        if (key === this.lastSeenKey) {
            return;
        }
        this.lastSeenKey = key;
        this.scheduleCheck();
    }

    scheduleCheck = () => {
        if (this.checkTimer) {
            clearTimeout(this.checkTimer);
        }
        this.checkTimer = setTimeout(this.runCheck, CHECK_DEBOUNCE_MS);
    }

    runCheck = async () => {
        const { animal, journalId, drugs, saleIsLocked } = this.props;
        const { prescriptionStore } = this.props.stores;
        if (saleIsLocked || !animal?.id || !journalId) {
            // Nothing can be checked any more; do not leave a spinner from an earlier request.
            if (this.state.checking) {
                this.setState({ checking: false });
            }
            return;
        }
        const key = drugSetKey(drugs);
        if (key === this.state.lastCheckedKey) {
            // Back to an already-checked set (e.g. A+B → A): nothing is pending for this set.
            this.setState({ checking: false });
            return;
        }
        if (!key) {
            this.setState({ checking: false, checkWarnings: [], checkError: '', lastCheckedKey: key });
            return;
        }
        this.setState({ checking: true, checkError: '' });
        const response = await prescriptionStore.aiCheck({
            animalId: animal.id,
            journalId,
            drugs: drugsToCheckPayload(drugs),
        });
        if (this.unmounted) {
            return;
        }
        if (drugSetKey(this.props.drugs) !== key) {
            // Stale: the drawer moved on while this request was in flight. Never let its
            // warnings land. If the current set already has a result, nothing else is pending.
            if (drugSetKey(this.props.drugs) === this.state.lastCheckedKey) {
                this.setState({ checking: false });
            }
            return;
        }
        if (response?.status === 200) {
            const data = response.data || {};
            this.setState(prev => ({
                checking: false,
                checkWarnings: data.warnings || [],
                currentMedications: data.currentMedications || prev.currentMedications,
                weight: data.weight || prev.weight,
                checkError: data.aiError ? 'ai' : '',
                lastCheckedKey: key,
            }));
        } else {
            this.setState({ checking: false, checkError: 'unavailable', lastCheckedKey: key });
        }
    }

    onClickPropose = async () => {
        const { animal, journalId, drugs } = this.props;
        const { prescriptionStore } = this.props.stores;
        if (!animal?.id || !journalId) {
            return;
        }
        // A proposal is written against the drawer as it was when the request left. If the vet
        // changes the drawer meanwhile, the answer no longer applies (it may even propose a drug
        // that is now already in the prescription) — discard it and ask for a fresh one.
        const startKey = drugSetKey(drugs);
        this.setState({
            loadingProposal: true,
            proposalDone: false,
            proposalError: '',
            proposals: [],
            proposalWarnings: [],
            summary: '',
            claudeLogId: 0,
            rating: 0,
            selectedPackage: {},
            accepted: {},
            dismissed: {},
        });
        const response = await prescriptionStore.aiProposal({
            animalId: animal.id,
            journalId,
            currentDrugs: drugsToCheckPayload(drugs),
        });
        if (this.unmounted) {
            return;
        }
        if (drugSetKey(this.props.drugs) !== startKey) {
            this.setState({ loadingProposal: false, proposalDone: true, proposalError: 'stale' });
            return;
        }
        if (response?.status === 200) {
            const data = response.data || {};
            this.setState(prev => ({
                loadingProposal: false,
                proposalDone: true,
                proposals: data.proposals || [],
                proposalWarnings: data.warnings || [],
                currentMedications: data.currentMedications || prev.currentMedications,
                weight: data.weight || prev.weight,
                summary: data.summary || '',
                claudeLogId: data.claudeLogId || 0,
            }));
        } else {
            this.setState({ loadingProposal: false, proposalDone: true, proposalError: response?.message || 'error' });
        }
    }

    onSelectPackage = (idx, lId) => {
        this.setState(prev => ({ selectedPackage: { ...prev.selectedPackage, [idx]: lId } }));
    }

    // The package the vet picked, or the one that may be preselected (single match, or a
    // match with the strength the model dosed for). Null when the vet still has to choose.
    chosenLId = (idx) => {
        const proposal = this.state.proposals[idx];
        return this.state.selectedPackage[idx] || preselectedLId(proposal);
    }

    selectedDrug = (idx) => {
        const proposal = this.state.proposals[idx];
        const matched = proposal?.matchedDrugs || [];
        const chosen = this.chosenLId(idx);
        if (!chosen) {
            return null;
        }
        return matched.find(d => d.lId === chosen) || null;
    }

    acceptedLIds = () => {
        const { proposals, accepted } = this.state;
        return proposals
            .map((p, idx) => (accepted[idx] ? this.chosenLId(idx) : null))
            .filter(Boolean);
    }

    onClickAccept = async (idx) => {
        const { onAcceptProposal = () => {} } = this.props;
        const { prescriptionStore } = this.props.stores;
        const drug = this.selectedDrug(idx);
        const proposal = this.state.proposals[idx];
        if (!drug || !proposal) {
            return;
        }
        await onAcceptProposal(drug, proposalToPrefill(proposal));
        if (this.unmounted) {
            return;
        }
        this.setState(prev => ({ accepted: { ...prev.accepted, [idx]: true } }), () => {
            const { claudeLogId, rating } = this.state;
            if (claudeLogId > 0) {
                prescriptionStore.aiFeedback({ claudeLogId, rating, acceptedLIds: this.acceptedLIds() });
            }
        });
    }

    onClickDismiss = (idx) => {
        this.setState(prev => ({ dismissed: { ...prev.dismissed, [idx]: true } }));
    }

    onClickRate = (rating) => {
        const { prescriptionStore } = this.props.stores;
        const { claudeLogId } = this.state;
        this.setState({ rating });
        if (claudeLogId > 0) {
            prescriptionStore.aiFeedback({ claudeLogId, rating, acceptedLIds: this.acceptedLIds() });
        }
    }

    onClickToggleMedications = () => {
        this.setState(prev => ({ showMedications: !prev.showMedications }));
    }

    renderWarning = (warning, idx) => {
        const { drugs = [] } = this.props;
        const spcLinks = drugs
            .filter(d => d.preparatomtaleavsnittLenke && (warning.drugs || []).some(name => name && (d.navnFormStyrke || '').indexOf(name) > -1))
            .map(d => d.preparatomtaleavsnittLenke);
        return (
            <div key={`${warning.source}-${idx}`} class={`alert ${severityClass(warning.severity)} py-2 px-3 mb-2 small`} role='alert'>
                <div class='d-flex flex-row align-items-start'>
                    <i class={`fa-solid ${warning.severity === 'high' ? 'fa-triangle-exclamation' : 'fa-circle-info'} me-2 mt-1`} />
                    <div class='flex-fill'>
                        {warning.drugs && warning.drugs.length > 0 && (
                            <strong class='me-1'>{warning.drugs.join(' + ')}:</strong>
                        )}
                        {warning.text}
                        <div class='mt-1'>
                            <span class='badge bg-secondary me-1'>
                                {warning.source === 'rule'
                                    ? <Text id='dyrejournal.prescription.ai.rule'>Rule</Text>
                                    : <Text id='dyrejournal.prescription.ai.ai'>AI</Text>}
                            </span>
                            {spcLinks.map(link => (
                                <a key={link} href={link} target='_blank' rel='noopener noreferrer' class='me-2'>
                                    <i class='fa-solid fa-file-lines me-1' />
                                    <Text id='dyrejournal.prescription.ai.spc'>SPC</Text>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderProposal = (proposal, idx) => {
        const { saleIsLocked } = this.props;
        const { accepted, dismissed, selectedPackage } = this.state;
        if (dismissed[idx]) {
            return null;
        }
        const matched = proposal.matchedDrugs || [];
        const chosenLId = selectedPackage[idx] || preselectedLId(proposal);
        const chosenDrug = matched.find(d => d.lId === chosenLId) || null;
        const strengthKnown = !!proposal.preferredStrength;
        const strengthMismatch = strengthKnown && chosenDrug && !strengthMatches(proposal.preferredStrength, chosenDrug.navnFormStyrke);
        const confidenceClass = proposal.confidence === 'high' ? 'bg-success' : (proposal.confidence === 'low' ? 'bg-secondary' : 'bg-warning text-dark');
        return (
            <div key={`proposal-${idx}`} class='card mb-2'>
                <div class='card-body py-2 px-3'>
                    <div class='d-flex flex-row justify-content-between align-items-start'>
                        <div>
                            <strong>{proposal.substance || matched[0]?.navnFormStyrke}</strong>
                            {proposal.dosePerKg && <span class='text-body-secondary ms-2 small'>{proposal.dosePerKg}</span>}
                            <span class={`badge ${confidenceClass} ms-2`}>
                                <Text id='dyrejournal.prescription.ai.confidence'>Confidence</Text>: {proposal.confidence}
                            </span>
                        </div>
                        {!accepted[idx] && (
                            <button type='button' class='btn btn-sm btn-link text-body-secondary py-0' onClick={() => this.onClickDismiss(idx)}>
                                <Text id='dyrejournal.prescription.ai.dismiss'>Dismiss</Text>
                            </button>
                        )}
                    </div>

                    {matched.length > 1 && (
                        <div class='mt-2'>
                            <small class='text-body-secondary d-block mb-1'>
                                {!chosenLId && strengthKnown ? (
                                    <Text id='dyrejournal.prescription.ai.chooseStrength' fields={{ strength: proposal.preferredStrength }}>
                                        Choose a package – the dose was computed for {proposal.preferredStrength}
                                    </Text>
                                ) : (
                                    <Text id='dyrejournal.prescription.ai.choosePackage'>Choose package</Text>
                                )}
                            </small>
                            <div class='d-flex flex-wrap gap-1'>
                                {matched.map(d => (
                                    <button
                                        key={d.lId}
                                        type='button'
                                        class={`btn btn-sm rounded-pill ${d.lId === chosenLId ? 'btn-primary' : 'btn-outline-primary'}`}
                                        style='font-size: 0.8em;'
                                        onClick={() => this.onSelectPackage(idx, d.lId)}
                                        disabled={accepted[idx]}
                                    >
                                        {packageLabel(d)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {matched.length === 1 && (
                        <div class='small text-body-secondary mt-1'>{packageLabel(matched[0])}</div>
                    )}
                    {strengthMismatch && (
                        <div class='alert alert-warning py-1 px-2 mt-2 mb-0 small'>
                            <i class='fa-solid fa-triangle-exclamation me-1' />
                            <Text id='dyrejournal.prescription.ai.strengthMismatch' fields={{ strength: proposal.preferredStrength }}>
                                The dosage was computed for strength {proposal.preferredStrength}. Check the dose for the selected package.
                            </Text>
                        </div>
                    )}
                    {matched.length === 0 && (
                        <div class='small text-warning mt-1'>
                            <i class='fa-solid fa-magnifying-glass me-1' />
                            <Text id='dyrejournal.prescription.ai.noPackage'>No FEST product found – search for the drug manually.</Text>
                        </div>
                    )}

                    <div class='small mt-2'>
                        {proposal.dosage && (
                            <div>
                                <strong><Text id='dyrejournal.prescription.dose'>Dssn.</Text>:</strong> {proposal.dosage}
                            </div>
                        )}
                        <div>
                            <strong><Text id='input.qty'>Qty</Text>:</strong> {proposal.qty}
                            {proposal.reit > 0 && (
                                <span class='ms-2'><strong><Text id='dyrejournal.prescription.reit'>REIT</Text>:</strong> {proposal.reit}</span>
                            )}
                            {proposal.durationDays > 0 && (
                                <span class='ms-2'>
                                    <Text id='dyrejournal.prescription.ai.durationDays' fields={{ days: proposal.durationDays }}>
                                        {proposal.durationDays} days
                                    </Text>
                                </span>
                            )}
                        </div>
                        {proposal.additionalNotes && <div class='text-body-secondary'>{proposal.additionalNotes}</div>}
                        {proposal.rationale && (
                            <div class='text-body-secondary mt-1'>
                                <i class='fa-solid fa-comment-medical me-1' />
                                <Text id='dyrejournal.prescription.ai.rationale'>Rationale</Text>: {proposal.rationale}
                            </div>
                        )}
                    </div>

                    <div class='mt-2'>
                        {accepted[idx] ? (
                            <span class='text-success small'>
                                <i class='fa-solid fa-check me-1' />
                                <Text id='dyrejournal.prescription.ai.added'>Added to prescription</Text>
                            </span>
                        ) : (
                            <button
                                type='button'
                                class='btn btn-sm btn-success rounded-pill'
                                onClick={() => this.onClickAccept(idx)}
                                disabled={saleIsLocked || !chosenLId}
                            >
                                <i class='fa-solid fa-plus me-1' />
                                <Text id='dyrejournal.prescription.ai.add'>Add</Text>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { animal, journalId, saleIsLocked } = this.props;
        const {
            loadingProposal, proposalDone, proposalError, proposals, proposalWarnings, checkWarnings, currentMedications,
            weight, summary, claudeLogId, rating, checking, checkError, showMedications, dismissed,
        } = this.state;

        const warnings = mergeWarnings(proposalWarnings, checkWarnings);
        const activeMedications = currentMedications.filter(m => m.active);
        const visibleProposals = proposals.filter((p, idx) => !dismissed[idx]);

        return (
            <div class='mb-3 p-2 border rounded bg-body-tertiary'>
                <div class='d-flex flex-row justify-content-between align-items-center mb-2'>
                    <div class='small text-body-secondary'>
                        <i class='fa-solid fa-wand-magic-sparkles me-1' />
                        <Text id='dyrejournal.prescription.ai.title'>AI prescription</Text>
                        {checking && (
                            <span class='ms-2 text-body-secondary'>
                                <i class='fa-solid fa-spinner fa-spin me-1' />
                                <Text id='dyrejournal.prescription.ai.checking'>Checking combinations...</Text>
                            </span>
                        )}
                        {!checking && checkError === 'unavailable' && (
                            <span class='ms-2 text-body-secondary'>
                                <Text id='dyrejournal.prescription.ai.checkUnavailable'>Interaction check unavailable</Text>
                            </span>
                        )}
                        {!checking && checkError === 'ai' && (
                            <span class='ms-2 text-body-secondary'>
                                <Text id='dyrejournal.prescription.ai.checkRulesOnly'>AI check failed – rule warnings only</Text>
                            </span>
                        )}
                    </div>
                    <button
                        type='button'
                        class='btn btn-sm btn-primary rounded-pill'
                        onClick={this.onClickPropose}
                        disabled={loadingProposal || saleIsLocked || !animal?.id || !journalId}
                    >
                        <i class={`fa-solid ${loadingProposal ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'} me-1`} />
                        {loadingProposal
                            ? <Text id='dyrejournal.prescription.ai.suggesting'>Generating proposal...</Text>
                            : <Text id='dyrejournal.prescription.ai.suggest'>Suggest prescription</Text>}
                    </button>
                </div>

                <div class='small text-body-secondary mb-2'>
                    <i class='fa-solid fa-triangle-exclamation me-1' />
                    <Text id='dyrejournal.prescription.ai.disclaimer'>AI-generated. Verify every proposal and warning against the journal and the SPC before prescribing.</Text>
                </div>

                {weight && weight.stale && (
                    <div class='alert alert-warning py-1 px-2 mb-2 small'>
                        <i class='fa-solid fa-weight-scale me-1' />
                        <Text id='dyrejournal.prescription.ai.weightStale'>Weight is missing or older than 6 months – consider weighing the animal.</Text>
                    </div>
                )}

                {currentMedications.length > 0 && (
                    <div class='mb-2'>
                        <div class='small text-body-secondary' style='cursor: pointer;' onClick={this.onClickToggleMedications}>
                            <i class={`fa-solid fa-chevron-${showMedications ? 'up' : 'down'} me-1`} />
                            <Text id='dyrejournal.prescription.ai.activeMedications'>Presumed active medications (latest record)</Text>
                            {' '}({activeMedications.length})
                        </div>
                        {showMedications && (
                            <div class='d-flex flex-wrap gap-1 mt-1'>
                                {currentMedications.map(m => (
                                    <span key={`${m.name}-${m.date}`} class={`badge rounded-pill ${m.active ? 'bg-primary' : 'bg-secondary'}`} style='font-weight: normal;'>
                                        {m.name}
                                        {m.dose && <span class='ms-1'>· {m.dose}</span>}
                                        {m.date && <span class='ms-1'>· {String(m.date).slice(0, 10)}</span>}
                                        <span class='ms-1'>· {(m.sources || []).map(s => SOURCE_LABEL[s] || s).join('/')}</span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {warnings.length > 0 && (
                    <div class='mb-2'>
                        {warnings.map(this.renderWarning)}
                    </div>
                )}

                {proposalError === 'stale' && (
                    <div class='alert alert-warning py-2 px-3 small mb-2'>
                        <i class='fa-solid fa-rotate me-1' />
                        <Text id='dyrejournal.prescription.ai.staleProposal'>The prescription changed while the proposal was being generated. Ask for a new proposal.</Text>
                    </div>
                )}

                {proposalError !== '' && proposalError !== 'stale' && (
                    <div class='alert alert-danger py-2 px-3 small mb-2'>
                        <i class='fa-solid fa-circle-exclamation me-1' />
                        {proposalError}
                    </div>
                )}

                {proposalDone && proposalError === '' && proposals.length === 0 && (
                    <div class='small text-body-secondary mb-2'>
                        <Text id='dyrejournal.prescription.ai.noProposals'>AI found no basis for a proposal in the journal.</Text>
                        {summary && <span class='ms-1'>{summary}</span>}
                    </div>
                )}

                {visibleProposals.length > 0 && (
                    <div class='mb-2'>
                        <div class='small text-body-secondary mb-1'>
                            <Text id='dyrejournal.prescription.ai.proposals'>Proposals</Text>
                            {summary && <span class='ms-1 text-body-secondary'>– {summary}</span>}
                        </div>
                        {proposals.map(this.renderProposal)}
                    </div>
                )}

                {claudeLogId > 0 && (
                    <div class='d-flex flex-row align-items-center small text-body-secondary'>
                        <span class='me-2'>
                            <Text id='dyrejournal.prescription.ai.rateQuestion'>Was this useful?</Text>
                        </span>
                        <button type='button' class={`btn btn-sm rounded-pill py-0 me-1 ${rating === 1 ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => this.onClickRate(1)}>
                            <i class='fa-solid fa-thumbs-up' />
                        </button>
                        <button type='button' class={`btn btn-sm rounded-pill py-0 ${rating === -1 ? 'btn-danger' : 'btn-outline-secondary'}`} onClick={() => this.onClickRate(-1)}>
                            <i class='fa-solid fa-thumbs-down' />
                        </button>
                        {rating !== 0 && (
                            <span class='ms-2'>
                                <Text id='dyrejournal.prescription.ai.thanks'>Thanks for the feedback</Text>
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default PrescriptionAiAssistant;
